'use client'

import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ReferralView, type ReferralViewLabels } from '@kiao-voice/ui-kit'
import { useReferral } from '@kiao-voice/hooks'
import { readStoredToken } from '@/features/auth/auth-storage'
import { fetchReferral } from '@/features/billing/api-client'
import { WorkspaceChip } from '@/components/workspace-indicator'

interface Props {
  /** Test seams. */
  getTokenFn?: () => string | null
  fetchReferralFn?: typeof fetchReferral
}

/** Build the share link from the origin the user is ACTUALLY on (matches the
 *  canonical site in prod; avoids a stale server WEB_APP_URL on preview hosts).
 *  The server `shareUrl` is only an SSR fallback. Mirrors the legacy panel. */
function buildShareUrl(code: string, serverShareUrl: string | null | undefined): string {
  if (typeof window === 'undefined') {
    return serverShareUrl ?? `/?ref=${encodeURIComponent(code)}`
  }
  return `${window.location.origin}/?ref=${encodeURIComponent(code)}`
}

/**
 * In-app referral page (`/app/referral`). Renders the ui-kit {@link ReferralView}
 * inside the web app shell (mounted by the (app)/layout). The full how-it-works steps + FAQ live ONLY on
 * the public `/referral` page (linked from the hero) to avoid duplicate copy.
 */
export function ReferralPage({
  getTokenFn = readStoredToken,
  fetchReferralFn = fetchReferral,
}: Props) {
  const { t } = useTranslation('web')
  const token = getTokenFn()
  const fetch = useCallback(() => fetchReferralFn(), [fetchReferralFn])
  const { referral, loading } = useReferral({ enabled: Boolean(token), fetchReferral: fetch })

  if (loading) {
    return (
      <p className="note" data-testid="referral-loading">
        {t('common.loading')}
      </p>
    )
  }

  // Flag off (or fetch failed) → the feature isn't available; show a neutral
  // notice rather than a half-built page (the nav item is always present).
  if (!referral || !referral.enabled || !referral.code) {
    return (
      <p className="note" data-testid="referral-disabled">
        {t('referral.disabledNote')}
      </p>
    )
  }

  const labels: ReferralViewLabels = {
    pageTitle: t('referral.pageTitle'),
    pageLead: t('referral.pageLead'),
    hero: {
      eyebrow: t('referral.heroEyebrow'),
      headline: t('referral.heroHeadline'),
      sub: t('referral.heroSub'),
      journeyShare: t('referral.journeyShare'),
      journeyFriend: t('referral.journeyFriend'),
      journeyReward: t('referral.journeyReward'),
      copy: t('referral.copyLink'),
      copied: t('referral.copyLinkDone'),
      more: t('referral.moreLink'),
    },
    statsHeading: t('referral.statsHeading'),
    statReferred: t('referral.statReferred'),
    statQualified: t('referral.statCompleted'),
    statEarned: t('referral.statEarned'),
  }

  return (
    <ReferralView
      labels={labels}
      data={{
        shareUrl: buildShareUrl(referral.code, referral.shareUrl),
        referredCount: referral.referredCount ?? 0,
        qualifiedCount: referral.qualifiedCount ?? 0,
        totalEarnedTwd: referral.totalEarnedTwd ?? 0,
      }}
      moreHref="/referral"
      moreTarget="_blank"
      headerActions={<WorkspaceChip />}
      formatEarned={(amount) => t('referral.earnedAmount', { amount })}
    />
  )
}
